import React, { useCallback, useEffect, useMemo, useState } from "react";
import API from "./api";
import { formatProfileNumberValue } from "./profileNumber";

const BRAND = {
  red: "#D73C2E",
  redDeep: "#A92B25",
  black: "#252935",
  sand: "#fff8f3",
  ivory: "#fffdfb",
  blush: "#fff1eb",
  green: "#1f8f5f",
  greenSoft: "#edf9f2",
  amber: "#b7791f",
  amberSoft: "#fff8e7",
  blue: "#2b5fb3",
  blueSoft: "#edf4ff",
  border: "rgba(185,52,58,0.14)",
  borderStrong: "rgba(185,52,58,0.24)",
  shadow: "rgba(37,41,53,0.08)",
  text: "#252935",
  muted: "#6b6f7d",
  mutedSoft: "#8b90a0",
};

const EMPTY_SUMMARY = {
  filters: {
    date_from: null,
    date_to: null,
    user_id: null,
    role_id: null,
  },
  overview: {
    active_users: 0,
    calls_made: 0,
    profiles_handled: 0,
    recommendations_made: 0,
    followups_scheduled: 0,
    followups_completed: 0,
    meetings_done: 0,
    profiles_matched: 0,
    meetings_converted: 0,
    tasks_assigned: 0,
    task_status_updates: 0,
    tasks_completed: 0,
    followup_completion_rate: null,
    meeting_conversion_rate: null,
  },
  trend: [],
  top_users: [],
  top_profiles: [],
  operations: {
    backlog: {
      snapshot: {
        active_profiles: 0,
        open_followups: 0,
        overdue_followups: 0,
        open_tasks: 0,
        overdue_tasks: 0,
        active_recommendations: 0,
        total_workload: 0,
        total_backlog: 0,
      },
      aging: [],
    },
    workload_by_user: [],
  },
};

const EMPTY_USERS = {
  filters: {
    date_from: null,
    date_to: null,
    user_id: null,
    role_id: null,
    limit: 30,
    offset: 0,
    sort_by: "meetings_converted",
    sort_dir: "desc",
  },
  total: 0,
  rows: [],
  policy: {
    max_results: null,
  },
};

const EMPTY_FILTERS = {
  users: [],
  roles: [],
};

const EMPTY_USER_HISTORY = {
  filters: {
    date_from: null,
    date_to: null,
    user_id: null,
    activity_date: null,
    action: null,
    limit: 80,
    offset: 0,
  },
  user: null,
  totals: {
    calls_made: 0,
    profiles_handled: 0,
    recommendations_made: 0,
    followups_scheduled: 0,
    followups_completed: 0,
    meetings_done: 0,
    meetings_converted: 0,
    profiles_matched: 0,
    tasks_assigned: 0,
    task_status_updates: 0,
    tasks_completed: 0,
    followup_completion_rate: null,
    meeting_conversion_rate: null,
  },
  selected_day: null,
  rows: [],
  references: {
    users: {},
    roles: {},
    user_types: {},
    profiles: {},
  },
  audit: {
    total: 0,
    limit: 80,
    offset: 0,
    has_more: false,
    items: [],
    action_options: [],
  },
};

const EMPTY_WORKLOAD_DETAIL = {
  filters: {
    date_from: null,
    date_to: null,
    user_id: null,
  },
  user: null,
  summary: {
    active_profiles: 0,
    open_followups: 0,
    overdue_followups: 0,
    open_tasks: 0,
    overdue_tasks: 0,
    active_recommendations: 0,
    recent_activity_7d: 0,
    activity_in_range: 0,
    last_activity_at: null,
    total_workload: 0,
    total_backlog: 0,
  },
  assigned_profiles: [],
  followups: [],
  tasks: [],
  recommendations: [],
};

const TEAM_COLUMNS = [
  { key: "email", label: "User" },
  { key: "role_name", label: "Role" },
  { key: "profiles_handled", label: "Profiles Visited" },
  { key: "unique_profiles_visited", label: "Unique Profiles" },
  { key: "productivity_score", label: "Productivity" },
  { key: "followup_completion_rate", label: "Follow-up Rate" },
  { key: "meetings_done", label: "Meetings" },
  { key: "meetings_converted", label: "Converted" },
  { key: "followups_completed", label: "Follow-ups" },
  { key: "tasks_completed", label: "Tasks Closed" },
  { key: "profiles_matched", label: "Matches" },
];

function getTodayDateString() {
  const formatter = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const parts = formatter.formatToParts(new Date());
  const year = parts.find((part) => part.type === "year")?.value || "0000";
  const month = parts.find((part) => part.type === "month")?.value || "01";
  const day = parts.find((part) => part.type === "day")?.value || "01";
  return `${year}-${month}-${day}`;
}

function shiftDate(value, days) {
  const [year, month, day] = String(value || "").split("-").map((part) => Number(part));
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "Not ready";
  return `${Number(value).toFixed(Number(value) % 1 === 0 ? 0 : 1)}%`;
}

function formatPointValue(value, formatAsPercent = false) {
  return formatAsPercent ? formatPercent(value) : formatNumber(value);
}

function calcRate(numerator, denominator) {
  const top = Number(numerator || 0);
  const bottom = Number(denominator || 0);
  if (!bottom) return null;
  return Number(((top * 100) / bottom).toFixed(2));
}

function calcProductivityPoints(row) {
  const profilesHandled = Number(row?.profiles_handled || 0);
  const recommendationsMade = Number(row?.recommendations_made || 0);
  const followupsCompleted = Number(row?.followups_completed || 0);
  const meetingsDone = Number(row?.meetings_done || 0);
  const meetingsConverted = Number(row?.meetings_converted || 0);
  const tasksCompleted = Number(row?.tasks_completed || 0);
  const profilesMatched = Number(row?.profiles_matched || 0);

  return (
    (profilesHandled * 1) +
    (recommendationsMade * 2) +
    (followupsCompleted * 3) +
    (meetingsDone * 4) +
    (meetingsConverted * 6) +
    (tasksCompleted * 3) +
    (profilesMatched * 8)
  );
}

function buildProductivityBreakdown(row) {
  const profilesHandled = Number(row?.profiles_handled || 0);
  const recommendationsMade = Number(row?.recommendations_made || 0);
  const followupsCompleted = Number(row?.followups_completed || 0);
  const meetingsDone = Number(row?.meetings_done || 0);
  const meetingsConverted = Number(row?.meetings_converted || 0);
  const tasksCompleted = Number(row?.tasks_completed || 0);
  const profilesMatched = Number(row?.profiles_matched || 0);

  return [
    `Handling a profile adds 1 point: ${formatNumber(profilesHandled)} profiles = ${formatNumber(profilesHandled * 1)} points`,
    `Giving a recommendation adds 2 points: ${formatNumber(recommendationsMade)} recommendations = ${formatNumber(recommendationsMade * 2)} points`,
    `Completing a follow-up adds 3 points: ${formatNumber(followupsCompleted)} follow-ups = ${formatNumber(followupsCompleted * 3)} points`,
    `Finishing a meeting adds 4 points: ${formatNumber(meetingsDone)} meetings = ${formatNumber(meetingsDone * 4)} points`,
    `Converting a meeting adds 6 points: ${formatNumber(meetingsConverted)} conversions = ${formatNumber(meetingsConverted * 6)} points`,
    `Closing a task adds 3 points: ${formatNumber(tasksCompleted)} tasks = ${formatNumber(tasksCompleted * 3)} points`,
    `Completing a match adds 8 points: ${formatNumber(profilesMatched)} matches = ${formatNumber(profilesMatched * 8)} points`,
  ];
}

function getInitials(name, fallback = "?") {
  const words = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return fallback;
  return `${words[0]?.[0] || ""}${words[1]?.[0] || words[0]?.[1] || ""}`.trim().toUpperCase() || fallback;
}

function pickColor(str) {
  const palette = ["#D73C2E", "#B9343A", "#C7433F", "#A93338", "#D1483A", "#252935"];
  let hash = 0;
  for (const char of String(str || "A")) hash = char.charCodeAt(0) + ((hash << 5) - hash);
  return palette[Math.abs(hash) % palette.length];
}

function getTrendTooltipLines(entry, point) {
  const lines = [
    `Date: ${formatDate(point.activity_date)}`,
    `${entry.label}: ${formatPointValue(point.value, entry.formatAsPercent)}`,
  ];
  const detailLines = typeof entry.describePoint === "function" ? entry.describePoint(point) : [];
  return [...lines, ...detailLines.filter(Boolean)];
}

function formatTrendTooltip(entry, point) {
  return getTrendTooltipLines(entry, point).join("\n");
}

function formatDate(value) {
  if (!value) return "-";
  const normalized = String(value).includes("T") ? String(value) : `${value}T00:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function formatShortDate(value) {
  if (!value) return "-";
  const normalized = String(value).includes("T") ? String(value) : `${value}T00:00:00`;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 10);
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

function formatDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatStatusChip(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "-";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function diffDaysFrom(dateValue, anchorValue) {
  if (!dateValue) return null;
  const left = new Date(dateValue);
  const right = anchorValue ? new Date(`${anchorValue}T00:00:00`) : new Date();
  if (Number.isNaN(left.getTime()) || Number.isNaN(right.getTime())) return null;
  const diff = right.getTime() - left.getTime();
  return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
}

function formatActionLabel(action) {
  const normalized = String(action || "").trim();
  if (!normalized) return "Activity";
  const actionLabels = {
    CREATE_FUTURE_PROSPECT: "Future Prospect Created",
    UPDATE_FUTURE_PROSPECT: "Future Prospect Updated",
    UPDATE_FUTURE_PROSPECT_STATUS: "Future Prospect Status Updated",
    VIEW_FUTURE_PROSPECTS_TAB: "Future Prospects Viewed",
  };
  if (actionLabels[normalized]) return actionLabels[normalized];
  return normalized
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatEntityLabel(entityType, entityId, item, references = {}) {
  if (item?.entity_display_name) return item.entity_display_name;
  const normalizedType = String(entityType || "").trim().toLowerCase();
  const entityKey = String(entityId || "");
  const payload = item?.new_data && typeof item.new_data === "object"
    ? item.new_data
    : item?.old_data && typeof item.old_data === "object"
      ? item.old_data
      : null;
  if (normalizedType === "user" && references.users?.[entityKey]) return references.users[entityKey];
  if (normalizedType === "role" && references.roles?.[entityKey]) return references.roles[entityKey];
  if (normalizedType === "user_type" && references.user_types?.[entityKey]) return references.user_types[entityKey];
  if (normalizedType === "profile" && references.profiles?.[entityKey]) return references.profiles[entityKey];
  if (normalizedType === "future_prospect" && payload) {
    const relativeName = String(payload.relative_name || "").trim();
    const profileKey = String(payload.profile_id || "").trim();
    const profileLabel = profileKey && references.profiles?.[profileKey]
      ? references.profiles[profileKey]
      : profileKey
        ? `Profile ${profileKey}`
        : "";
    const combined = [relativeName, profileLabel].filter(Boolean).join(" | ");
    if (combined) return combined;
  }
  const typeLabel = String(entityType || "record")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
  return entityId ? `${typeLabel} #${entityId}` : typeLabel;
}

function labelizeFieldName(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "Field";
  return normalized
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function labelizeAuditFieldName(value) {
  const normalized = String(value || "").trim().toLowerCase();
  const friendlyMap = {
    user_type_id: "User Type",
    role_id: "Role",
    role_ids: "Roles",
    parent_role_id: "Parent Role",
    user_id: "User",
    assigned_to_user_id: "Assigned To",
    assigned_by_user_id: "Assigned By",
    follow_up_completed_by: "Completed By",
    profile_id: "Profile",
    to_profile_id: "Recommended Profile",
    from_profile_id: "Source Profile",
    related_profile_id: "Related Profile",
  };
  return friendlyMap[normalized] || labelizeFieldName(value);
}

const HIDDEN_AUDIT_FIELDS = new Set([
  "activity_date",
  "audit_count",
  "audit_limit",
  "audit_offset",
  "count",
  "created_at",
  "created_by",
  "date_from",
  "date_to",
  "id",
  "include_meta",
  "limit",
  "offset",
  "refresh",
  "updated_at",
  "user_id",
]);

function shouldShowAuditField(key, item) {
  const normalized = String(key || "").trim().toLowerCase();
  if (!normalized) return false;
  const action = String(item?.action || "").trim().toUpperCase();
  if (action.startsWith("VIEW_")) return false;
  if (HIDDEN_AUDIT_FIELDS.has(normalized)) return false;
  if (normalized.endsWith("_at")) return false;
  return true;
}

function resolveReferenceLabel(fieldKey, value, references = {}) {
  const normalizedKey = String(fieldKey || "").trim().toLowerCase();
  const keyValue = String(value || "").trim();
  if (!keyValue) return null;
  if (normalizedKey === "user_type_id") return references.user_types?.[keyValue] || null;
  if (normalizedKey === "role_id" || normalizedKey === "parent_role_id") return references.roles?.[keyValue] || null;
  if (normalizedKey === "role_ids") return references.roles?.[keyValue] || null;
  if (normalizedKey === "user_id" || normalizedKey.endsWith("_user_id")) return references.users?.[keyValue] || null;
  if (
    normalizedKey === "profile_id"
    || normalizedKey === "to_profile_id"
    || normalizedKey === "from_profile_id"
    || normalizedKey === "related_profile_id"
    || normalizedKey.endsWith("_profile_id")
  ) {
    return references.profiles?.[keyValue] || null;
  }
  return null;
}

function formatAuditValue(value, fieldKey = "", references = {}) {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    return value.length
      ? value.map((item) => formatAuditValue(item, fieldKey, references)).join(", ")
      : "-";
  }
  const referenceLabel = resolveReferenceLabel(fieldKey, value, references);
  if (referenceLabel) return referenceLabel;
  if (typeof value === "object") {
    return Object.entries(value)
      .map(([key, item]) => `${labelizeAuditFieldName(key)}: ${formatAuditValue(item, key, references)}`)
      .join(" | ");
  }
  return String(value).replace(/_/g, " ");
}

function describeAuditChanges(item, references = {}) {
  const oldData = item?.old_data && typeof item.old_data === "object" ? item.old_data : {};
  const newData = item?.new_data && typeof item.new_data === "object" ? item.new_data : {};
  const keys = Array.from(new Set([
    ...Object.keys(oldData),
    ...Object.keys(newData),
  ])).filter((key) => shouldShowAuditField(key, item));

  if (!keys.length) {
    return [];
  }

  return keys.map((key) => {
    const hasOld = Object.prototype.hasOwnProperty.call(oldData, key);
    const hasNew = Object.prototype.hasOwnProperty.call(newData, key);
    return {
      key,
      label: labelizeAuditFieldName(key),
      oldValue: hasOld ? formatAuditValue(oldData[key], key, references) : null,
      newValue: hasNew ? formatAuditValue(newData[key], key, references) : null,
      type: hasOld && hasNew ? "changed" : hasNew ? "added" : "removed",
    };
  });
}

function MetricCard({ label, value, hint, tone = "default" }) {
  const toneStyle = tone === "success"
    ? {
        background: BRAND.greenSoft,
        borderColor: "rgba(31,143,95,0.18)",
      }
    : tone === "warning"
      ? {
          background: BRAND.amberSoft,
          borderColor: "rgba(183,121,31,0.18)",
        }
      : tone === "info"
        ? {
            background: BRAND.blueSoft,
            borderColor: "rgba(43,95,179,0.18)",
          }
        : {
            background: BRAND.blush,
            borderColor: BRAND.border,
          };

  return (
    <div style={{ ...S.metricCard, ...toneStyle }}>
      <div style={S.metricLabel}>{label}</div>
      <div style={S.metricValue}>{value}</div>
      <div style={S.metricHint}>{hint}</div>
    </div>
  );
}

function buildLinePath(points) {
  if (!points.length) return "";
  return points.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
}

function buildSmoothLinePath(points) {
  if (!points.length) return "";
  if (points.length < 3) return buildLinePath(points);

  let path = `M ${points[0].x} ${points[0].y}`;
  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const previous = points[index - 1] || current;
    const afterNext = points[index + 2] || next;
    const controlPoint1X = current.x + ((next.x - previous.x) / 6);
    const controlPoint1Y = current.y + ((next.y - previous.y) / 6);
    const controlPoint2X = next.x - ((afterNext.x - current.x) / 6);
    const controlPoint2Y = next.y - ((afterNext.y - current.y) / 6);
    path += ` C ${controlPoint1X} ${controlPoint1Y}, ${controlPoint2X} ${controlPoint2Y}, ${next.x} ${next.y}`;
  }
  return path;
}

function buildAreaPath(points, bottomY) {
  if (!points.length) return "";
  const linePath = buildSmoothLinePath(points);
  const last = points[points.length - 1];
  const first = points[0];
  return `${linePath} L ${last.x} ${bottomY} L ${first.x} ${bottomY} Z`;
}

function buildYAxisTicks(maxValue) {
  if (maxValue <= 1) return [0, 1];
  if (maxValue <= 4) return Array.from({ length: maxValue + 1 }, (_, index) => index);
  const rawTicks = [0, 0.25, 0.5, 0.75, 1].map((step) => Math.round(maxValue * step));
  return Array.from(new Set(rawTicks)).sort((left, right) => left - right);
}

function TrendChart({ data, series, title, subtitle }) {
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const width = 760;
  const height = 300;
  const padding = { top: 22, right: 34, bottom: 62, left: 56 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  const usableData = Array.isArray(data) ? data : [];
  const maxValue = Math.max(
    1,
    ...usableData.flatMap((item) => series.map((entry) => Number(item?.[entry.key] || 0)))
  );
  const yTicks = buildYAxisTicks(maxValue);
  const pointsBySeries = series.map((entry) => {
    const points = usableData.map((item, index) => {
      const x = padding.left + (usableData.length <= 1 ? chartWidth / 2 : (index / (usableData.length - 1)) * chartWidth);
      const y = padding.top + chartHeight - ((Number(item?.[entry.key] || 0) / maxValue) * chartHeight);
      return {
        x,
        y,
        value: Number(item?.[entry.key] || 0),
        label: item?.activity_date,
        activity_date: item?.activity_date,
        item,
      };
    });
    return {
      ...entry,
      points,
      linePath: buildSmoothLinePath(points),
      areaPath: entry.fill ? buildAreaPath(points, padding.top + chartHeight) : "",
    };
  });
  const xLabels = usableData.filter((_, index) => usableData.length <= 4 || index === 0 || index === usableData.length - 1 || index === Math.floor((usableData.length - 1) / 2));

  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.legendRow}>
          {series.map((entry) => (
            <span key={entry.key} style={S.legendItem}>
              <span style={{ ...S.legendSwatch, background: entry.color }} />
              {entry.label}
            </span>
          ))}
        </div>
      </div>
      {!usableData.length ? (
        <div style={S.chartEmpty}>No trend data available for this range.</div>
      ) : (
        <div style={S.chartCanvas} onMouseLeave={() => setHoveredPoint(null)}>
          <svg viewBox={`0 0 ${width} ${height}`} style={S.chartSvg} role="img" aria-label={title}>
            <line x1={padding.left} y1={padding.top + chartHeight} x2={padding.left + chartWidth} y2={padding.top + chartHeight} stroke="rgba(43,95,179,0.26)" strokeWidth="2" />
            <line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + chartHeight} stroke="rgba(43,95,179,0.26)" strokeWidth="2" />
            {usableData.map((item, index) => {
              const x = padding.left + (usableData.length <= 1 ? chartWidth / 2 : (index / Math.max(usableData.length - 1, 1)) * chartWidth);
              return (
                <line
                  key={`v-${String(item.activity_date)}`}
                  x1={x}
                  y1={padding.top}
                  x2={x}
                  y2={padding.top + chartHeight}
                  stroke="rgba(37,41,53,0.05)"
                  strokeWidth="1"
                  strokeDasharray="2 6"
                />
              );
            })}
            {yTicks.map((tick) => {
              const y = padding.top + chartHeight - ((tick / maxValue) * chartHeight);
              return (
                <g key={tick}>
                  <line x1={padding.left} y1={y} x2={padding.left + chartWidth} y2={y} stroke="rgba(37,41,53,0.08)" strokeWidth="1" strokeDasharray="3 5" />
                  <text x={padding.left + chartWidth + 4} y={y + 4} fill={BRAND.mutedSoft} fontSize="10">
                    {tick}
                  </text>
                </g>
              );
            })}
            {pointsBySeries.map((entry) => (
              <g key={entry.key}>
                {entry.areaPath ? <path d={entry.areaPath} fill={entry.fill} opacity="0.22" /> : null}
                <path d={entry.linePath} fill="none" stroke="rgba(37,41,53,0.12)" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" transform="translate(0 2)" />
                <path d={entry.linePath} fill="none" stroke={entry.color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                {entry.points.map((point) => (
                  <circle
                    key={`${entry.key}-${point.x}`}
                    cx={point.x}
                    cy={point.y}
                    r="4"
                    fill="#fff"
                    stroke={entry.color}
                    strokeWidth="2"
                    onMouseEnter={() => setHoveredPoint({ entry, point })}
                  >
                    <title>{formatTrendTooltip(entry, point)}</title>
                  </circle>
                ))}
              </g>
            ))}
            {xLabels.map((item) => {
              const index = usableData.indexOf(item);
              const x = padding.left + (usableData.length <= 1 ? chartWidth / 2 : (index / (usableData.length - 1)) * chartWidth);
              return (
                <text key={String(item.activity_date)} x={x} y={height - 28} fill={BRAND.mutedSoft} fontSize="11" textAnchor="middle">
                  {formatShortDate(item.activity_date)}
                </text>
              );
            })}
            <text x={padding.left + (chartWidth / 2)} y={height - 6} fill={BRAND.mutedSoft} fontSize="12" fontWeight="700" textAnchor="middle">
              X Axis: Date
            </text>
            <text
              x={18}
              y={padding.top + (chartHeight / 2)}
              fill={BRAND.mutedSoft}
              fontSize="12"
              fontWeight="700"
              textAnchor="middle"
              transform={`rotate(-90 18 ${padding.top + (chartHeight / 2)})`}
            >
              Y Axis: Count
            </text>
          </svg>
          {hoveredPoint && (
            <div
              style={{
                ...S.chartTooltip,
                left: `${(hoveredPoint.point.x / width) * 100}%`,
                top: `${(hoveredPoint.point.y / height) * 100}%`,
              }}
            >
              {getTrendTooltipLines(hoveredPoint.entry, hoveredPoint.point).map((line) => (
                <div key={line} style={line.startsWith("Date:") ? S.chartTooltipSub : S.chartTooltipLine}>{line}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

function ProductivityScoreboard({ rows, title, subtitle }) {
  const [showHelp, setShowHelp] = useState(false);
  const items = (rows || []).slice(0, 8).map((row) => ({
    id: row.user_id,
    label: String(row.email || "").split("@")[0] || row.email || "User",
    email: row.email || "User",
    role: row.role_name || "User",
    score: Number(row.productivity_score || 0),
    points: Number(row.productivity_points || 0),
    matches: Number(row.profiles_matched || 0),
    converted: Number(row.meetings_converted || 0),
    tasks: Number(row.tasks_completed || 0),
    breakdown: buildProductivityBreakdown(row),
  }));
  const maxValue = Math.max(1, ...items.map((item) => item.score));

  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderActions}>
          <div style={S.productivityHeaderChip}>Top users in this range</div>
          <button
            type="button"
            style={S.infoButton}
            aria-expanded={showHelp}
            aria-label="Show how productivity score is calculated"
            onClick={() => setShowHelp((current) => !current)}
          >
            i
          </button>
        </div>
      </div>
      {showHelp && (
        <div style={S.helpCard}>
          <div style={S.infoTitle}>How to read this score</div>
          <div style={S.infoText}>
            This is a comparison score, not a pass or fail grade. We give more credit to stronger outcomes, so a completed match helps more than a routine action.
          </div>
          <div style={S.infoList}>
            <div>Handling a profile adds 1 point.</div>
            <div>Giving a recommendation adds 2 points.</div>
            <div>Completing a follow-up adds 3 points.</div>
            <div>Finishing a meeting adds 4 points.</div>
            <div>Converting a meeting adds 6 points.</div>
            <div>Closing a task adds 3 points.</div>
            <div>Completing a match adds 8 points.</div>
          </div>
          <div style={S.infoText}>
            The user with the highest total points in the selected date range becomes 100%. Everyone else is shown relative to that top user.
          </div>
          <div style={S.infoText}>
            Example: if one user has 50% and the top user has 100%, it means the first user delivered about half as much weighted output in this filtered range.
          </div>
        </div>
      )}
      {!items.length ? (
        <div style={S.chartEmpty}>No user data available.</div>
      ) : (
        <div style={S.productivityList}>
          {items.map((item) => (
            <div key={item.id} style={S.productivityRow}>
              <div style={S.productivityIdentity}>
                <div style={S.productivityName}>{item.label}</div>
                <div style={S.productivityMeta}>{item.role} | {formatNumber(item.converted)} converted | {formatNumber(item.matches)} matches | {formatNumber(item.tasks)} tasks closed</div>
              </div>
              <div style={S.productivityBarGroup}>
                <span style={S.productivityValue}>{Math.round(item.score)}%</span>
                <div style={S.productivityTrack}>
                  <div style={{ ...S.productivityFill, width: `${(item.score / maxValue) * 100}%` }} title={[`${item.email}`, `Raw points: ${formatNumber(item.points)}`, ...item.breakdown].join("\n")} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TopProfilesPanel({ profiles, title, subtitle }) {
  const items = Array.isArray(profiles) ? profiles : [];

  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderChip}>Top 5 this range</div>
      </div>
      {!items.length ? (
        <div style={S.chartEmpty}>No profile activity found for this range.</div>
      ) : (
        <div style={S.profileSpotlightList}>
          {items.map((profile, index) => {
            const name = profile.profile_name || "Unnamed profile";
            const initials = getInitials(name, String(profile.profile_no || profile.profile_id || "?").slice(0, 2));
            const avatarColor = pickColor(name || profile.profile_no || profile.profile_id || "P");
            return (
              <div key={profile.profile_id || `${profile.profile_no}-${index}`} style={S.profileSpotlightRow}>
                <div style={S.profileSpotlightIdentity}>
                  {profile.photo ? (
                    <img src={profile.photo} alt={name} style={S.profileSpotlightPhoto} />
                  ) : (
                    <div style={{ ...S.profileSpotlightAvatar, background: avatarColor }}>
                      <span style={S.profileSpotlightInitials}>{initials}</span>
                    </div>
                  )}
                  <div style={S.profileSpotlightCopy}>
                    <div style={S.profileSpotlightName}>{name}</div>
                    <div style={S.profileSpotlightMeta}>
                      #{formatProfileNumberValue(profile.profile_no, profile.gender, "-")} | ID {profile.profile_id || "-"}
                    </div>
                  </div>
                </div>
                <div style={S.profileSpotlightCount}>
                  <div style={S.profileSpotlightCountValue}>{formatNumber(profile.activity_count)}</div>
                  <div style={S.profileSpotlightCountLabel}>activities</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function BacklogAgingPanel({ backlog, title, subtitle }) {
  const snapshot = backlog?.snapshot || {};
  const aging = Array.isArray(backlog?.aging) ? backlog.aging : [];
  const maxBucket = Math.max(1, ...aging.map((row) => Number(row.total || 0)));
  const overdueTotal = Number(snapshot.overdue_followups || 0) + Number(snapshot.overdue_tasks || 0);

  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderChip}>Live open workload</div>
      </div>

      <div style={S.backlogSnapshotGrid}>
        <div style={S.backlogSnapshotCard}>
          <div style={S.backlogSnapshotLabel}>Total Backlog</div>
          <div style={S.backlogSnapshotValue}>{formatNumber(snapshot.total_backlog)}</div>
          <div style={S.backlogSnapshotHint}>Open follow-ups, tasks, and active recommendations</div>
        </div>
        <div style={S.backlogSnapshotCard}>
          <div style={S.backlogSnapshotLabel}>Overdue Items</div>
          <div style={S.backlogSnapshotValue}>{formatNumber(overdueTotal)}</div>
          <div style={S.backlogSnapshotHint}>Delayed follow-ups and past-due tasks</div>
        </div>
        <div style={S.backlogSnapshotCard}>
          <div style={S.backlogSnapshotLabel}>Assigned Profiles</div>
          <div style={S.backlogSnapshotValue}>{formatNumber(snapshot.active_profiles)}</div>
          <div style={S.backlogSnapshotHint}>Profiles still sitting in active ownership</div>
        </div>
      </div>

      <div style={S.legendRow}>
        <span style={S.legendItem}><span style={{ ...S.legendSwatch, background: BRAND.blue }} />Follow-ups</span>
        <span style={S.legendItem}><span style={{ ...S.legendSwatch, background: BRAND.redDeep }} />Tasks</span>
        <span style={S.legendItem}><span style={{ ...S.legendSwatch, background: BRAND.green }} />Recommendations</span>
      </div>

      {!aging.length ? (
        <div style={S.chartEmpty}>No backlog aging data is available for this range.</div>
      ) : (
        <div style={S.backlogAgingList}>
          {aging.map((row) => {
            const total = Number(row.total || 0);
            const rowWidth = Math.max(total ? (total / maxBucket) * 100 : 0, total ? 8 : 0);
            const followups = Number(row.followups || 0);
            const tasks = Number(row.tasks || 0);
            const recommendations = Number(row.recommendations || 0);
            return (
              <div key={row.bucket} style={S.backlogAgingRow}>
                <div style={S.backlogAgingMeta}>
                  <div style={S.backlogAgingLabel}>{row.bucket}</div>
                  <div style={S.backlogAgingHint}>
                    {formatNumber(total)} open items
                  </div>
                </div>
                <div style={S.backlogAgingTrack}>
                  {total > 0 && (
                    <div style={{ ...S.backlogAgingFill, width: `${rowWidth}%` }}>
                      {followups > 0 && (
                        <div
                          style={{ ...S.backlogSegment, width: `${(followups / total) * 100}%`, background: BRAND.blue }}
                          title={`Follow-ups: ${formatNumber(followups)}`}
                        />
                      )}
                      {tasks > 0 && (
                        <div
                          style={{ ...S.backlogSegment, width: `${(tasks / total) * 100}%`, background: BRAND.redDeep }}
                          title={`Tasks: ${formatNumber(tasks)}`}
                        />
                      )}
                      {recommendations > 0 && (
                        <div
                          style={{ ...S.backlogSegment, width: `${(recommendations / total) * 100}%`, background: BRAND.green }}
                          title={`Recommendations: ${formatNumber(recommendations)}`}
                        />
                      )}
                    </div>
                  )}
                </div>
                <div style={S.backlogAgingSplit}>
                  {formatNumber(followups)} F
                  {" · "}
                  {formatNumber(tasks)} T
                  {" · "}
                  {formatNumber(recommendations)} R
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ClosureEfficiencyPanel({ rows, title, subtitle }) {
  const items = Array.isArray(rows) ? rows.slice(0, 6) : [];
  const maxValue = Math.max(1, ...items.map((item) => Number(item.closure_efficiency || 0)));

  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderChip}>Closed vs still pending</div>
      </div>
      {!items.length ? (
        <div style={S.chartEmpty}>No closure efficiency rows are available.</div>
      ) : (
        <div style={S.productivityList}>
          {items.map((item) => (
            <div key={item.user_id} style={S.productivityRow}>
              <div style={S.productivityIdentity}>
                <div style={S.productivityName}>{item.email}</div>
                <div style={S.productivityMeta}>
                  {item.role_name} | {formatNumber(item.closed_units)} closures | {formatNumber(item.live_backlog)} live backlog | {formatNumber(item.active_profiles)} assigned profiles
                </div>
              </div>
              <div style={S.productivityBarGroup}>
                <span style={S.productivityValue}>{formatPercent(item.closure_efficiency)}</span>
                <div style={S.productivityTrack}>
                  <div
                    style={{ ...S.productivityFill, width: `${(Number(item.closure_efficiency || 0) / maxValue) * 100}%` }}
                    title={`Closures: ${formatNumber(item.closed_units)} | Live backlog: ${formatNumber(item.live_backlog)}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function DependencyLoadPanel({ rows, title, subtitle, onSelect }) {
  const items = Array.isArray(rows) ? rows.slice(0, 6) : [];
  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderChip}>Live workload share</div>
      </div>
      {!items.length ? (
        <div style={S.chartEmpty}>No live workload is assigned in this scope.</div>
      ) : (
        <div style={S.productivityList}>
          {items.map((item) => (
            <button key={item.user_id} type="button" style={S.workloadButton} onClick={() => onSelect && onSelect(item)}>
              <div style={S.productivityRow}>
                <div style={S.productivityIdentity}>
                  <div style={S.productivityName}>{item.email}</div>
                  <div style={S.productivityMeta}>
                    {item.role_name} | {formatNumber(item.total_workload)} live items | {formatNumber(item.active_profiles)} profiles | {formatNumber(item.open_tasks)} open tasks
                  </div>
                </div>
                <div style={S.productivityBarGroup}>
                  <span style={S.productivityValue}>{formatPercent(item.share_percent)}</span>
                  <div style={S.productivityTrack}>
                    <div
                      style={{ ...S.productivityFill, width: `${Math.min(Number(item.share_percent || 0), 100)}%` }}
                      title={`Profiles: ${formatNumber(item.active_profiles)} | Follow-ups: ${formatNumber(item.open_followups)} | Tasks: ${formatNumber(item.open_tasks)} | Recommendations: ${formatNumber(item.active_recommendations)}`}
                    />
                  </div>
                </div>
                <div style={S.workloadHint}>Click to view live workload details</div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function LowActivityAlertsPanel({ rows, title, subtitle }) {
  const items = Array.isArray(rows) ? rows.slice(0, 6) : [];
  return (
    <section style={S.chartPanel}>
      <div style={S.chartHeader}>
        <div>
          <h3 style={S.chartTitle}>{title}</h3>
          <p style={S.chartSub}>{subtitle}</p>
        </div>
        <div style={S.productivityHeaderChip}>Needs attention</div>
      </div>
      {!items.length ? (
        <div style={S.chartEmpty}>No low-activity alerts in the current scope.</div>
      ) : (
        <div style={S.alertList}>
          {items.map((item) => (
            <div key={item.user_id} style={S.alertCard}>
              <div style={S.alertHeader}>
                <div>
                  <div style={S.alertTitle}>{item.email}</div>
                  <div style={S.alertMeta}>{item.role_name}</div>
                </div>
                <div style={item.recent_activity_7d === 0 ? S.alertBadgeCritical : S.alertBadgeWarning}>
                  {item.recent_activity_7d === 0 ? "No recent activity" : "Low recent activity"}
                </div>
              </div>
              <div style={S.alertBody}>
                {formatNumber(item.total_workload)} live items are still sitting with this user.
                {item.last_activity_at
                  ? ` Last action was ${formatNumber(item.days_since_last_activity)} day(s) ago.`
                  : " No activity has been logged in the selected lookback."}
              </div>
              <div style={S.alertBreakdown}>
                Profiles {formatNumber(item.active_profiles)} · Follow-ups {formatNumber(item.open_followups)} · Tasks {formatNumber(item.open_tasks)} · Recommendations {formatNumber(item.active_recommendations)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default function HrmsAnalytics({ canRefresh = false }) {
  const defaultRange = useMemo(() => {
    const dateTo = getTodayDateString();
    const dateFrom = shiftDate(dateTo, -29);
    return { date_from: dateFrom, date_to: dateTo };
  }, []);

  const [filters, setFilters] = useState({
    date_from: defaultRange.date_from,
    date_to: defaultRange.date_to,
    user_id: "",
    role_id: "",
    sort_by: "meetings_converted",
    sort_dir: "desc",
  });
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [usersReport, setUsersReport] = useState(EMPTY_USERS);
  const [filterOptions, setFilterOptions] = useState(EMPTY_FILTERS);
  const [historyUser, setHistoryUser] = useState(null);
  const [historyDay, setHistoryDay] = useState("");
  const [historyAction, setHistoryAction] = useState("");
  const [historyReport, setHistoryReport] = useState(EMPTY_USER_HISTORY);
  const [historyPanels, setHistoryPanels] = useState({
    analytics: true,
    audit: true,
  });
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [workloadUser, setWorkloadUser] = useState(null);
  const [workloadDetail, setWorkloadDetail] = useState(EMPTY_WORKLOAD_DETAIL);
  const [workloadLoading, setWorkloadLoading] = useState(false);
  const [workloadError, setWorkloadError] = useState("");
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState(false);
  const [error, setError] = useState("");

  const selectedUserOption = useMemo(
    () => filterOptions.users.find((item) => String(item.id) === String(filters.user_id || "")) || null,
    [filterOptions.users, filters.user_id]
  );
  const selectedRoleOption = useMemo(
    () => filterOptions.roles.find((item) => String(item.id) === String(filters.role_id || "")) || null,
    [filterOptions.roles, filters.role_id]
  );

  const buildParams = useCallback(() => ({
    date_from: filters.date_from,
    date_to: filters.date_to,
    user_id: filters.user_id || undefined,
    role_id: filters.role_id || undefined,
    sort_by: filters.sort_by,
    sort_dir: filters.sort_dir,
    limit: 100,
  }), [filters]);

  const loadSummaryAndUsers = useCallback(async ({ rebuild = false } = {}) => {
    const params = buildParams();

    setError("");
    if (rebuild) {
      setRebuilding(true);
      await API.post("/reports/hrms-analytics/rebuild", {
        date_from: filters.date_from,
        date_to: filters.date_to,
        user_id: filters.user_id || undefined,
      });
    }

    const [summaryResponse, usersResponse] = await Promise.all([
      API.get("/reports/hrms-analytics/summary", { params }),
      API.get("/reports/hrms-analytics/users", { params }),
    ]);

    setSummary(summaryResponse.data || EMPTY_SUMMARY);
    setUsersReport(usersResponse.data || EMPTY_USERS);
  }, [buildParams, filters.date_from, filters.date_to, filters.user_id]);

  const loadUserHistory = useCallback(async (userId, { activityDate = "", action = historyAction, offset = 0, append = false } = {}) => {
    if (!userId) return;
    setHistoryLoading(true);
    setHistoryError("");
    try {
      const response = await API.get(`/reports/hrms-analytics/users/${userId}/history`, {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
          activity_date: activityDate || undefined,
          action: action || undefined,
          limit: 80,
          offset,
        },
      });
      const nextReport = response.data || EMPTY_USER_HISTORY;
      setHistoryReport((current) => {
        if (!append) return nextReport;
        return {
          ...nextReport,
          audit: {
            ...(nextReport.audit || {}),
            items: [
              ...((current.audit && Array.isArray(current.audit.items)) ? current.audit.items : []),
              ...((nextReport.audit && Array.isArray(nextReport.audit.items)) ? nextReport.audit.items : []),
            ],
          },
        };
      });
    } catch (err) {
      setHistoryError(err?.response?.data?.error || "Failed to load user history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [filters.date_from, filters.date_to, historyAction]);

  useEffect(() => {
    let mounted = true;

    const run = async () => {
      setLoading(true);
      try {
        const initialParams = {
          date_from: defaultRange.date_from,
          date_to: defaultRange.date_to,
          sort_by: "meetings_converted",
          sort_dir: "desc",
          limit: 100,
        };
        const [filtersResponse, summaryResponse, usersResponse] = await Promise.all([
          API.get("/reports/hrms-analytics/filters"),
          API.get("/reports/hrms-analytics/summary", { params: initialParams }),
          API.get("/reports/hrms-analytics/users", { params: initialParams }),
        ]);
        if (!mounted) return;
        setFilterOptions(filtersResponse.data || EMPTY_FILTERS);
        setSummary(summaryResponse.data || EMPTY_SUMMARY);
        setUsersReport(usersResponse.data || EMPTY_USERS);
      } catch (err) {
        if (mounted) {
          setError(err?.response?.data?.error || "Failed to load HRMS analytics.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setRebuilding(false);
        }
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [defaultRange.date_from, defaultRange.date_to]);

  const handleFilterChange = (field) => (event) => {
    const nextValue = event.target.value;
    setFilters((current) => ({
      ...current,
      [field]: nextValue,
    }));
  };

  const handleApply = async (event) => {
    event.preventDefault();
    setLoading(true);
    setHistoryUser(null);
    setHistoryDay("");
    setHistoryAction("");
    setHistoryReport(EMPTY_USER_HISTORY);
    try {
      await loadSummaryAndUsers();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to refresh HRMS analytics.");
    } finally {
      setLoading(false);
      setRebuilding(false);
    }
  };

  const handleManualRebuild = async () => {
    setLoading(true);
    try {
      await loadSummaryAndUsers({ rebuild: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to rebuild HRMS analytics.");
    } finally {
      setLoading(false);
      setRebuilding(false);
    }
  };

  const clearFilters = async () => {
    const nextFilters = {
      date_from: defaultRange.date_from,
      date_to: defaultRange.date_to,
      user_id: "",
      role_id: "",
      sort_by: "meetings_converted",
      sort_dir: "desc",
    };
    setFilters(nextFilters);
    setHistoryUser(null);
    setHistoryDay("");
    setHistoryAction("");
    setHistoryReport(EMPTY_USER_HISTORY);
    setLoading(true);
    setError("");
    try {
      const params = {
        date_from: nextFilters.date_from,
        date_to: nextFilters.date_to,
        sort_by: nextFilters.sort_by,
        sort_dir: nextFilters.sort_dir,
        limit: 100,
      };
      const [summaryResponse, usersResponse] = await Promise.all([
        API.get("/reports/hrms-analytics/summary", { params }),
        API.get("/reports/hrms-analytics/users", { params }),
      ]);
      setSummary(summaryResponse.data || EMPTY_SUMMARY);
      setUsersReport(usersResponse.data || EMPTY_USERS);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to reset HRMS analytics filters.");
    } finally {
      setLoading(false);
      setRebuilding(false);
    }
  };

  const openHistoryModal = async (row) => {
    setHistoryUser(row);
    setHistoryDay("");
    setHistoryAction("");
    setHistoryReport(EMPTY_USER_HISTORY);
    setHistoryPanels({ analytics: true, audit: true });
    await loadUserHistory(row.user_id, { action: "" });
  };

  const closeHistoryModal = () => {
    setHistoryUser(null);
    setHistoryDay("");
    setHistoryAction("");
    setHistoryReport(EMPTY_USER_HISTORY);
    setHistoryPanels({ analytics: true, audit: true });
    setHistoryError("");
  };

  const openWorkloadModal = async (row) => {
    setWorkloadUser(row);
    setWorkloadDetail(EMPTY_WORKLOAD_DETAIL);
    setWorkloadError("");
    setWorkloadLoading(true);
    try {
      const response = await API.get(`/reports/hrms-analytics/users/${row.user_id}/workload`, {
        params: {
          date_from: filters.date_from,
          date_to: filters.date_to,
        },
      });
      setWorkloadDetail(response.data || EMPTY_WORKLOAD_DETAIL);
    } catch (err) {
      setWorkloadError(err?.response?.data?.error || "Failed to load workload details.");
    } finally {
      setWorkloadLoading(false);
    }
  };

  const closeWorkloadModal = () => {
    setWorkloadUser(null);
    setWorkloadDetail(EMPTY_WORKLOAD_DETAIL);
    setWorkloadError("");
    setWorkloadLoading(false);
  };

  const handleHistoryDayChange = async (event) => {
    const nextDay = event.target.value;
    setHistoryDay(nextDay);
    if (!historyUser?.user_id) return;
    await loadUserHistory(historyUser.user_id, { activityDate: nextDay });
  };

  const handleHistoryActionChange = async (event) => {
    const nextAction = event.target.value;
    setHistoryAction(nextAction);
    if (!historyUser?.user_id) return;
    await loadUserHistory(historyUser.user_id, { activityDate: historyDay, action: nextAction });
  };

  const loadMoreHistoryAudit = async () => {
    if (!historyUser?.user_id || !historyReport.audit?.has_more || historyLoading) return;
    await loadUserHistory(historyUser.user_id, {
      activityDate: historyDay,
      action: historyAction,
      offset: Number(historyReport.audit?.offset || 0) + Number(historyReport.audit?.items?.length || 0),
      append: true,
    });
  };

  const toggleHistoryPanel = (panelKey) => {
    setHistoryPanels((current) => ({
      ...current,
      [panelKey]: !current[panelKey],
    }));
  };

  const overview = summary.overview || EMPTY_SUMMARY.overview;
  const operations = summary.operations || EMPTY_SUMMARY.operations;
  const workloadRows = useMemo(
    () => (Array.isArray(operations.workload_by_user) ? operations.workload_by_user : []),
    [operations.workload_by_user]
  );
  const trendRows = useMemo(
    () => [...(summary.trend || [])].sort((left, right) => String(left.activity_date).localeCompare(String(right.activity_date))),
    [summary.trend]
  );
  const rankedUsers = useMemo(() => {
    const rows = Array.isArray(usersReport.rows) ? usersReport.rows : [];
    const rowsWithPoints = rows.map((row) => ({
      ...row,
      productivity_points: calcProductivityPoints(row),
    }));
    const maxPoints = Math.max(1, ...rowsWithPoints.map((row) => Number(row.productivity_points || 0)));

    return rowsWithPoints
      .map((row) => ({
        ...row,
        productivity_score: Number((((Number(row.productivity_points || 0) / maxPoints) * 100)).toFixed(1)),
      }))
      .sort((left, right) => (
        Number(right.productivity_score || 0) - Number(left.productivity_score || 0)
        || Number(right.meetings_converted || 0) - Number(left.meetings_converted || 0)
        || Number(right.profiles_matched || 0) - Number(left.profiles_matched || 0)
        || String(left.email || "").localeCompare(String(right.email || ""))
      ));
  }, [usersReport.rows]);
  const closureRows = useMemo(() => {
    const combined = new Map();
    workloadRows.forEach((row) => {
      combined.set(String(row.user_id), {
        ...row,
        followups_completed: 0,
        tasks_completed: 0,
        meetings_done: 0,
        profiles_matched: 0,
      });
    });
    rankedUsers.forEach((row) => {
      const key = String(row.user_id);
      combined.set(key, {
        ...(combined.get(key) || {}),
        ...row,
        active_profiles: Number(combined.get(key)?.active_profiles || 0),
        open_followups: Number(combined.get(key)?.open_followups || 0),
        open_tasks: Number(combined.get(key)?.open_tasks || 0),
        active_recommendations: Number(combined.get(key)?.active_recommendations || 0),
      });
    });
    return Array.from(combined.values())
      .map((row) => {
        const liveBacklog = Number(row.open_followups || 0) + Number(row.open_tasks || 0) + Number(row.active_recommendations || 0);
        const closedUnits = Number(row.followups_completed || 0) + Number(row.tasks_completed || 0) + Number(row.meetings_done || 0) + Number(row.profiles_matched || 0);
        const denominator = closedUnits + liveBacklog;
        return {
          ...row,
          live_backlog: liveBacklog,
          closed_units: closedUnits,
          closure_efficiency: denominator > 0 ? Number(((closedUnits * 100) / denominator).toFixed(1)) : 0,
        };
      })
      .filter((row) => row.closed_units > 0 || row.live_backlog > 0 || Number(row.active_profiles || 0) > 0)
      .sort((left, right) => (
        Number(right.closure_efficiency || 0) - Number(left.closure_efficiency || 0)
        || Number(right.closed_units || 0) - Number(left.closed_units || 0)
        || Number(left.live_backlog || 0) - Number(right.live_backlog || 0)
      ));
  }, [rankedUsers, workloadRows]);
  const dependencyRows = useMemo(() => {
    const totalWorkload = workloadRows.reduce((sum, row) => sum + Number(row.total_workload || 0), 0);
    return workloadRows
      .filter((row) => Number(row.total_workload || 0) > 0)
      .map((row) => ({
        ...row,
        share_percent: totalWorkload > 0 ? Number((((Number(row.total_workload || 0) * 100) / totalWorkload)).toFixed(1)) : 0,
      }))
      .sort((left, right) => (
        Number(right.total_workload || 0) - Number(left.total_workload || 0)
        || Number(right.open_tasks || 0) - Number(left.open_tasks || 0)
        || String(left.email || "").localeCompare(String(right.email || ""))
      ));
  }, [workloadRows]);
  const lowActivityAlerts = useMemo(() => workloadRows
    .map((row) => ({
      ...row,
      days_since_last_activity: diffDaysFrom(row.last_activity_at, filters.date_to),
    }))
    .filter((row) => Number(row.total_workload || 0) >= 3 && Number(row.recent_activity_7d || 0) <= 2)
    .sort((left, right) => (
      Number(right.total_workload || 0) - Number(left.total_workload || 0)
      || Number(left.recent_activity_7d || 0) - Number(right.recent_activity_7d || 0)
      || (Number(right.days_since_last_activity || 0) - Number(left.days_since_last_activity || 0))
    )), [workloadRows, filters.date_to]);
  const averageProductivityScore = rankedUsers.length
    ? Number((rankedUsers.reduce((sum, row) => sum + Number(row.productivity_score || 0), 0) / rankedUsers.length).toFixed(1))
    : 0;
  const topProductivityUser = rankedUsers[0] || null;
  const matchImpactRate = calcRate(overview.profiles_matched, overview.recommendations_made);
  const taskClosureRate = calcRate(overview.tasks_completed, overview.tasks_assigned);
  const highlightCards = [
    {
      label: "Avg Productivity",
      value: `${averageProductivityScore.toFixed(averageProductivityScore % 1 === 0 ? 0 : 1)}%`,
      hint: topProductivityUser ? `Top user: ${topProductivityUser.email}` : "No active users in this scope",
      tone: "info",
    },
    {
      label: "Follow-up Discipline",
      value: formatPercent(overview.followup_completion_rate),
      hint: `${formatNumber(overview.followups_completed)} completed from ${formatNumber(overview.followups_scheduled)} scheduled`,
      tone: "default",
    },
    {
      label: "Meetings Done",
      value: formatNumber(overview.meetings_done),
      hint: "Meetings completed in the selected range",
      tone: "success",
    },
    {
      label: "Task Closure",
      value: formatPercent(taskClosureRate),
      hint: `${formatNumber(overview.tasks_completed)} closed from ${formatNumber(overview.tasks_assigned)} assigned`,
      tone: "warning",
    },
    {
      label: "Match Impact",
      value: formatPercent(matchImpactRate),
      hint: `${formatNumber(overview.profiles_matched)} matches from ${formatNumber(overview.recommendations_made)} recommendations`,
      tone: "success",
    },
  ];
  const historyScope = historyDay
    ? (historyReport.selected_day || EMPTY_USER_HISTORY.totals)
    : (historyReport.totals || EMPTY_USER_HISTORY.totals);
  const visibleHistoryRows = historyDay
    ? historyReport.rows.filter((row) => String(row.activity_date) === String(historyDay))
    : historyReport.rows;

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 1080px) {
          .jk-hrms-visual-grid,
          .jk-hrms-secondary-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
      <div style={S.hero}>
        <div style={S.heroCopy}>
          <div style={S.eyebrow}>HRMS Analytics</div>
          <h1 style={S.title}>Team Performance Dashboard</h1>
          <p style={S.subtitle}>
            Track team productivity, workflow quality, and key profile outcomes in one live operational view.
          </p>
        </div>

        <form style={S.filterCard} onSubmit={handleApply}>
          <div style={S.filterHeader}>
            <div>
              <div style={S.filterTitle}>Focus the report</div>
              <div style={S.filterSub}>Choose date range, user, or role instead of typing free-text search.</div>
            </div>
          </div>

          <div style={S.filterGrid}>
            <label style={S.field}>
              <span style={S.fieldLabel}>Date From</span>
              <input type="date" value={filters.date_from} onChange={handleFilterChange("date_from")} style={S.input} />
            </label>
            <label style={S.field}>
              <span style={S.fieldLabel}>Date To</span>
              <input type="date" value={filters.date_to} onChange={handleFilterChange("date_to")} style={S.input} />
            </label>
            <label style={S.field}>
              <span style={S.fieldLabel}>User</span>
              <select value={filters.user_id} onChange={handleFilterChange("user_id")} style={S.input}>
                <option value="">All users</option>
                {filterOptions.users.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.email}{item.role_name ? ` | ${item.role_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label style={S.field}>
              <span style={S.fieldLabel}>Role</span>
              <select value={filters.role_id} onChange={handleFilterChange("role_id")} style={S.input}>
                <option value="">All roles</option>
                {filterOptions.roles.map((item) => (
                  <option key={item.id} value={String(item.id)}>
                    {item.name}{item.parent_role_name ? ` | ${item.parent_role_name}` : ""}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div style={S.filterPills}>
            <span style={S.pill}>Range: {filters.date_from} to {filters.date_to}</span>
            {selectedUserOption && <span style={S.pill}>User: {selectedUserOption.email}</span>}
            {selectedRoleOption && <span style={S.pill}>Role: {selectedRoleOption.name}</span>}
          </div>

          <div style={S.filterActions}>
            <div style={S.actionCluster}>
              <button type="submit" style={S.primaryBtn} disabled={loading || rebuilding}>
                {loading ? "Loading..." : "Apply Filters"}
              </button>
              {canRefresh && (
                <button type="button" style={S.ghostBtn} disabled={loading || rebuilding} onClick={handleManualRebuild}>
                  {rebuilding ? "Refreshing..." : "Refresh"}
                </button>
              )}
              <button type="button" style={S.textBtn} disabled={loading || rebuilding} onClick={clearFilters}>
                Reset
              </button>
            </div>
          </div>
        </form>
      </div>

      {error && <div style={S.errorBanner}>{error}</div>}

      <div style={S.highlightGrid}>
        {highlightCards.map((item) => (
          <MetricCard key={item.label} label={item.label} value={item.value} hint={item.hint} tone={item.tone} />
        ))}
      </div>

      <div style={S.visualGrid} className="jk-hrms-visual-grid">
        <div style={S.chartStack}>
          <TrendChart
            data={trendRows}
            title="Daily Team Pulse"
            subtitle="How follow-up completions, meetings, and task closures move across the selected range."
            series={[
              { key: "followups_completed", label: "Follow-ups Done", color: BRAND.blue, fill: "rgba(43,95,179,0.18)" },
              { key: "meetings_done", label: "Meetings Done", color: BRAND.redDeep, fill: null },
              { key: "tasks_completed", label: "Tasks Closed", color: BRAND.green, fill: null },
            ]}
          />

          <TrendChart
            data={trendRows}
            title="Profile Movement"
            subtitle="Profiles added and matched across the selected range. Hover a point to see the profile names tied to that day."
            series={[
              {
                key: "profiles_created",
                label: "Profiles Added",
                color: BRAND.amber,
                fill: null,
                describePoint: (point) => {
                  const labels = Array.isArray(point.item?.profiles_created_details)
                    ? point.item.profiles_created_details.map((entry) => entry?.label).filter(Boolean)
                    : [];
                  return labels.length ? ["Profiles:", ...labels] : ["No profile names captured for this day."];
                },
              },
              {
                key: "profiles_matched",
                label: "Profiles Matched",
                color: BRAND.green,
                fill: null,
                describePoint: (point) => {
                  const labels = Array.isArray(point.item?.profiles_matched_details)
                    ? point.item.profiles_matched_details.map((entry) => entry?.label).filter(Boolean)
                    : [];
                  return labels.length ? ["Matched profiles:", ...labels] : ["No matched profile names captured for this day."];
                },
              },
            ]}
          />

          <section style={S.panel}>
            <div style={S.sectionHeader}>
              <div>
                <h2 style={S.sectionTitle}>Team Leaderboard</h2>
                <p style={S.sectionSub}>
                  Ranked by delivery quality and user output in the selected scope.
                </p>
              </div>
              <div style={S.sectionMeta}>
                {usersReport.total ? `${formatNumber(usersReport.total)} users` : "No users found"}
              </div>
            </div>

            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    {TEAM_COLUMNS.map((column) => (
                      <th key={column.key} style={S.th}>{column.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rankedUsers.map((row) => (
                    <tr key={row.user_id} style={S.row}>
                      <td style={S.userCell}>
                        <button type="button" style={S.userButton} onClick={() => openHistoryModal(row)}>
                          <span style={S.userEmail}>{row.email}</span>
                          <span style={S.userMeta}>{row.user_type_name || "Team member"} | View history</span>
                        </button>
                      </td>
                      <td style={S.td}>{row.role_name || "User"}</td>
                      <td style={S.td}>{formatNumber(row.profiles_handled)}</td>
                      <td style={S.td}>{formatNumber(row.unique_profiles_visited)}</td>
                      <td style={S.td}>{formatPercent(row.productivity_score)}</td>
                      <td style={S.td}>{formatPercent(row.followup_completion_rate)}</td>
                      <td style={S.td}>{formatNumber(row.meetings_done)}</td>
                      <td style={S.td}>{formatNumber(row.meetings_converted)}</td>
                      <td style={S.td}>{formatNumber(row.followups_completed)}</td>
                      <td style={S.td}>{formatNumber(row.tasks_completed)}</td>
                      <td style={S.td}>{formatNumber(row.profiles_matched)}</td>
                    </tr>
                  ))}
                  {!rankedUsers.length && (
                    <tr>
                      <td style={S.emptyCell} colSpan={TEAM_COLUMNS.length}>
                        No leaderboard rows are available for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        <div style={S.sideStack}>
          <ProductivityScoreboard
            rows={rankedUsers}
            title="Productivity Scoreboard"
            subtitle="A quick ranking of users based on meaningful work done in the selected range."
          />
          <TopProfilesPanel
            profiles={summary.top_profiles}
            title="Top Active Profiles"
            subtitle="The 5 profiles with the most logged activity in this selected range."
          />
        </div>
      </div>

      <div style={S.secondaryGrid} className="jk-hrms-secondary-grid">
        <BacklogAgingPanel
          backlog={operations.backlog}
          title="Backlog Pressure"
          subtitle="Live open work split by age buckets so you can see where follow-ups, tasks, and recommendation pipeline are starting to pile up."
        />
        <ClosureEfficiencyPanel
          rows={closureRows}
          title="Closure Efficiency"
          subtitle="Which users are converting active work into finished outcomes instead of carrying forward more pending items."
        />
        <DependencyLoadPanel
          rows={dependencyRows}
          title="Dependency Load"
          subtitle="How much of the current live workload depends on each user right now."
          onSelect={openWorkloadModal}
        />
        <LowActivityAlertsPanel
          rows={lowActivityAlerts}
          title="Low Activity Alerts"
          subtitle="Users who still hold meaningful workload but have shown very little recent movement."
        />
      </div>

      {workloadUser && (
        <div style={S.modalBackdrop} onClick={(event) => event.target === event.currentTarget && closeWorkloadModal()}>
          <div style={S.modalCard}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalEyebrow}>Dependency Workload</div>
                <h3 style={S.modalTitle}>{workloadDetail.user?.email || workloadUser.email}</h3>
                <p style={S.modalSubtitle}>
                  {(workloadDetail.user?.role_name || workloadUser.role_name || "User")} | Live workload as of {formatDate(filters.date_to)}
                </p>
              </div>
              <button type="button" style={S.modalClose} onClick={closeWorkloadModal}>×</button>
            </div>

            <div style={S.modalFilterRow}>
              <div style={S.modalExplain}>
                Dependency load means how much current open work is still sitting with this user right now: assigned profiles, open follow-ups, open tasks, and active recommendations.
              </div>
            </div>

            {workloadError && <div style={S.errorBanner}>{workloadError}</div>}

            <div style={S.metricsGrid}>
              <MetricCard label="Total Workload" value={formatNumber(workloadDetail.summary.total_workload)} hint="All current live items with this user" tone="info" />
              <MetricCard label="Assigned Profiles" value={formatNumber(workloadDetail.summary.active_profiles)} hint="Profiles still owned by this user" />
              <MetricCard label="Open Follow-ups" value={formatNumber(workloadDetail.summary.open_followups)} hint={`${formatNumber(workloadDetail.summary.overdue_followups)} overdue`} tone="warning" />
              <MetricCard label="Open Tasks" value={formatNumber(workloadDetail.summary.open_tasks)} hint={`${formatNumber(workloadDetail.summary.overdue_tasks)} overdue`} tone="warning" />
              <MetricCard label="Active Recommendations" value={formatNumber(workloadDetail.summary.active_recommendations)} hint="Recommendations not yet closed" tone="success" />
              <MetricCard label="Recent Activity" value={formatNumber(workloadDetail.summary.recent_activity_7d)} hint="Actions in the last 7 days" />
            </div>

            <div style={S.modalBody}>
              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Assigned Profiles</div>
                    <div style={S.modalPanelSub}>Profiles that are still actively sitting with this user.</div>
                  </div>
                  <div style={S.sectionMeta}>{formatNumber(workloadDetail.assigned_profiles.length)} shown</div>
                </div>
                {workloadLoading ? <div style={S.emptyAudit}>Loading workload...</div> : (
                  <div style={S.workloadGrid}>
                    {workloadDetail.assigned_profiles.length ? workloadDetail.assigned_profiles.map((item) => (
                      <div key={item.profile_id} style={S.workloadCard}>
                        <div style={S.workloadTitle}>{item.profile_name}</div>
                        <div style={S.workloadMeta}>#{formatProfileNumberValue(item.profile_no, "", "-")} | ID {item.profile_id}</div>
                        <div style={S.workloadBody}>Assigned on {formatDateTime(item.assigned_at)}</div>
                      </div>
                    )) : <div style={S.emptyAudit}>No active profile assignments for this user.</div>}
                  </div>
                )}
              </section>

              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Open Follow-ups</div>
                    <div style={S.modalPanelSub}>Scheduled follow-ups that are still pending.</div>
                  </div>
                  <div style={S.sectionMeta}>{formatNumber(workloadDetail.followups.length)} shown</div>
                </div>
                {workloadLoading ? <div style={S.emptyAudit}>Loading workload...</div> : (
                  <div style={S.workloadGrid}>
                    {workloadDetail.followups.length ? workloadDetail.followups.map((item) => (
                      <div key={item.note_id} style={S.workloadCard}>
                        <div style={S.workloadTitle}>{item.profile_name}</div>
                        <div style={S.workloadMeta}>#{formatProfileNumberValue(item.profile_no, "", "-")} | Due {formatDateTime(item.follow_up_at)}</div>
                        <div style={S.workloadBody}>{item.content || "No note summary available."}</div>
                      </div>
                    )) : <div style={S.emptyAudit}>No open follow-ups for this user.</div>}
                  </div>
                )}
              </section>

              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Open Tasks</div>
                    <div style={S.modalPanelSub}>Tasks that are still pending or in progress.</div>
                  </div>
                  <div style={S.sectionMeta}>{formatNumber(workloadDetail.tasks.length)} shown</div>
                </div>
                {workloadLoading ? <div style={S.emptyAudit}>Loading workload...</div> : (
                  <div style={S.workloadGrid}>
                    {workloadDetail.tasks.length ? workloadDetail.tasks.map((item) => (
                      <div key={item.task_id} style={S.workloadCard}>
                        <div style={S.workloadTitle}>{item.title}</div>
                        <div style={S.workloadMeta}>{formatStatusChip(item.status)}{item.due_date ? ` | Due ${formatDate(item.due_date)}` : ""}</div>
                        <div style={S.workloadBody}>
                          {item.profile_name ? `Linked profile: ${item.profile_name}${item.profile_no ? ` (#${formatProfileNumberValue(item.profile_no)})` : ""}` : "No profile linked"}
                        </div>
                      </div>
                    )) : <div style={S.emptyAudit}>No open tasks for this user.</div>}
                  </div>
                )}
              </section>

              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Active Recommendations</div>
                    <div style={S.modalPanelSub}>Recommendation pipeline still waiting to be closed.</div>
                  </div>
                  <div style={S.sectionMeta}>{formatNumber(workloadDetail.recommendations.length)} shown</div>
                </div>
                {workloadLoading ? <div style={S.emptyAudit}>Loading workload...</div> : (
                  <div style={S.workloadGrid}>
                    {workloadDetail.recommendations.length ? workloadDetail.recommendations.map((item) => (
                      <div key={item.recommendation_id} style={S.workloadCard}>
                        <div style={S.workloadTitle}>{item.source_profile_name}</div>
                        <div style={S.workloadMeta}>
                          #{formatProfileNumberValue(item.source_profile_no, "", "-")} → #{formatProfileNumberValue(item.target_profile_no, "", "-")} | {formatStatusChip(item.status)}
                        </div>
                        <div style={S.workloadBody}>
                          Target: {item.target_profile_name} | Created {formatDateTime(item.created_at)}
                        </div>
                      </div>
                    )) : <div style={S.emptyAudit}>No active recommendations for this user.</div>}
                  </div>
                )}
              </section>
            </div>
          </div>
        </div>
      )}

      {historyUser && (
        <div style={S.modalBackdrop} onClick={(event) => event.target === event.currentTarget && closeHistoryModal()}>
          <div style={S.modalCard}>
            <div style={S.modalHeader}>
              <div>
                <div style={S.modalEyebrow}>User History</div>
                <h3 style={S.modalTitle}>{historyReport.user?.email || historyUser.email}</h3>
                <p style={S.modalSubtitle}>
                  {historyReport.user?.role_name || historyUser.role_name || "User"} | {formatDate(filters.date_from)} to {formatDate(filters.date_to)}
                </p>
              </div>
              <button type="button" style={S.modalClose} onClick={closeHistoryModal}>×</button>
            </div>

            <div style={S.modalFilterRow}>
              <label style={S.modalField}>
                <span style={S.fieldLabel}>Filter By Day</span>
                <input
                  type="date"
                  value={historyDay}
                  min={filters.date_from}
                  max={filters.date_to}
                  onChange={handleHistoryDayChange}
                  style={S.input}
                />
              </label>
              <button type="button" style={S.ghostBtn} onClick={() => {
                setHistoryDay("");
                if (historyUser?.user_id) {
                  loadUserHistory(historyUser.user_id, { activityDate: "", action: historyAction });
                }
              }}>
                All Days
              </button>
            </div>

            {historyError && <div style={S.errorBanner}>{historyError}</div>}

            <div style={S.metricsGrid}>
              <MetricCard label={historyDay ? "Calls On Day" : "Calls In Range"} value={formatNumber(historyScope.calls_made)} hint="Call activity for the current scope" />
              <MetricCard label="Profiles Handled" value={formatNumber(historyScope.profiles_handled)} hint="Distinct profiles touched" />
              <MetricCard label="Recommendations" value={formatNumber(historyScope.recommendations_made)} hint="Recommendations created" />
              <MetricCard label="Follow-up Rate" value={formatPercent(historyScope.followup_completion_rate)} hint="Completed vs scheduled" tone="info" />
              <MetricCard label="Meetings Done" value={formatNumber(historyScope.meetings_done)} hint="Meetings completed" />
              <MetricCard label="Meetings Converted" value={formatNumber(historyScope.meetings_converted)} hint="Meetings moved ahead successfully" tone="info" />
              <MetricCard label="Tasks Completed" value={formatNumber(historyScope.tasks_completed)} hint="Tasks closed in this scope" tone="success" />
              <MetricCard label="Matches" value={formatNumber(historyScope.profiles_matched)} hint="Matched outcomes recorded" tone="success" />
            </div>

            <div style={S.modalBody}>
              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Day-wise Analytics</div>
                    <div style={S.modalPanelSub}>One row per day from the saved `user_daily_metrics` table.</div>
                  </div>
                  <div style={S.sectionMeta}>
                    {historyDay ? formatDate(historyDay) : `${visibleHistoryRows.length} days`}
                  </div>
                  <button type="button" style={S.panelToggle} onClick={() => toggleHistoryPanel("analytics")}>
                    {historyPanels.analytics ? "Collapse" : "Expand"}
                  </button>
                </div>
                {historyPanels.analytics && (
                  <div style={S.tableWrap}>
                    <table style={S.table}>
                      <thead>
                        <tr>
                          <th style={S.th}>Date</th>
                          <th style={S.th}>Calls</th>
                          <th style={S.th}>Meetings</th>
                          <th style={S.th}>Converted</th>
                          <th style={S.th}>Follow-ups</th>
                          <th style={S.th}>Tasks Closed</th>
                          <th style={S.th}>Matches</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleHistoryRows.map((row) => (
                          <tr key={row.activity_date} style={S.row}>
                            <td style={S.td}>{formatDate(row.activity_date)}</td>
                            <td style={S.td}>{formatNumber(row.calls_made)}</td>
                            <td style={S.td}>{formatNumber(row.meetings_done)}</td>
                            <td style={S.td}>{formatNumber(row.meetings_converted)}</td>
                            <td style={S.td}>{formatNumber(row.followups_completed)}</td>
                            <td style={S.td}>{formatNumber(row.tasks_completed)}</td>
                            <td style={S.td}>{formatNumber(row.profiles_matched)}</td>
                          </tr>
                        ))}
                        {!visibleHistoryRows.length && (
                          <tr>
                            <td style={S.emptyCell} colSpan={7}>
                              No analytics rows found for this day.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section style={S.modalPanel}>
                <div style={S.modalPanelHeader}>
                  <div>
                    <div style={S.modalPanelTitle}>Audit Timeline</div>
                    <div style={S.modalPanelSub}>Human-readable activity changes for this user in the selected range.</div>
                  </div>
                  <div style={S.auditHeaderTools}>
                    <label style={S.auditFilterField}>
                      <span style={S.fieldLabel}>Action</span>
                      <select value={historyAction} onChange={handleHistoryActionChange} style={S.input}>
                        <option value="">All actions</option>
                        {(historyReport.audit?.action_options || []).map((option) => (
                          <option key={option.action} value={option.action}>
                            {formatActionLabel(option.action)} ({formatNumber(option.total)})
                          </option>
                        ))}
                      </select>
                    </label>
                    <div style={S.sectionMeta}>
                      {formatNumber(historyReport.audit?.total || 0)} activities
                    </div>
                  </div>
                  <button type="button" style={S.panelToggle} onClick={() => toggleHistoryPanel("audit")}>
                    {historyPanels.audit ? "Collapse" : "Expand"}
                  </button>
                </div>
                {historyPanels.audit && (
                  <>
                    <div style={S.auditList}>
                      {historyLoading && !historyReport.audit?.items?.length && (
                        <div style={S.emptyAudit}>Loading user history...</div>
                      )}
                      {!historyLoading && !historyReport.audit?.items?.length && (
                        <div style={S.emptyAudit}>No audit activity found for this selection.</div>
                      )}
                      {(historyReport.audit?.items || []).map((item) => {
                        const changes = describeAuditChanges(item, historyReport.references || EMPTY_USER_HISTORY.references);
                        return (
                          <article key={item.id} style={S.auditItem}>
                            <div style={S.auditHead}>
                              <span style={S.auditBadge}>{formatActionLabel(item.action)}</span>
                              <span style={S.auditTime}>{formatDateTime(item.created_at)}</span>
                            </div>
                            <div style={S.auditMeta}>{formatEntityLabel(item.entity_type, item.entity_id, item, historyReport.references || EMPTY_USER_HISTORY.references)}</div>
                            {changes.length > 0 && (
                              <details style={S.auditDetails}>
                                <summary style={S.auditSummary}>View changes</summary>
                                <div style={S.auditChangeList}>
                                  {changes.map((change) => (
                                    <div key={`${item.id}-${change.key}`} style={S.auditChangeRow}>
                                      <div style={S.auditChangeLabel}>{change.label}</div>
                                      <div style={S.auditChangeValue}>
                                        {change.type === "changed" ? `${change.oldValue || "-"} -> ${change.newValue || "-"}` : change.type === "added" ? change.newValue || "-" : change.oldValue || "-"}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </details>
                            )}
                          </article>
                        );
                      })}
                    </div>
                    {historyReport.audit?.has_more && (
                      <button type="button" style={S.ghostBtn} onClick={loadMoreHistoryAudit} disabled={historyLoading}>
                        {historyLoading ? "Loading..." : "Load More Activity"}
                      </button>
                    )}
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const S = {
  page: {
    display: "grid",
    gap: 24,
  },
  hero: {
    display: "grid",
    gap: 18,
  },
  heroCopy: {
    display: "grid",
    gap: 10,
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    padding: "7px 12px",
    borderRadius: 999,
    background: BRAND.blush,
    color: BRAND.red,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
  },
  title: {
    margin: 0,
    fontSize: "clamp(2rem, 3vw, 2.9rem)",
    lineHeight: 1.05,
    color: BRAND.text,
    maxWidth: 780,
  },
  subtitle: {
    margin: 0,
    color: BRAND.muted,
    maxWidth: 860,
    fontSize: 15,
    lineHeight: 1.7,
  },
  filterCard: {
    display: "grid",
    gap: 16,
    padding: 22,
    borderRadius: 28,
    background: `linear-gradient(180deg, ${BRAND.ivory}, ${BRAND.sand})`,
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: `0 20px 48px ${BRAND.shadow}`,
  },
  filterHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  filterTitle: {
    fontSize: 22,
    fontWeight: 800,
    color: BRAND.text,
  },
  filterSub: {
    marginTop: 6,
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.redDeep,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  field: {
    display: "grid",
    gap: 8,
  },
  fieldLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  input: {
    width: "100%",
    minHeight: 48,
    borderRadius: 16,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    padding: "0 14px",
    outline: "none",
    fontSize: 15,
    boxSizing: "border-box",
    appearance: "none",
  },
  filterPills: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.text,
    fontSize: 13,
    fontWeight: 700,
  },
  filterActions: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "center",
    flexWrap: "wrap",
  },
  actionCluster: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  primaryBtn: {
    minHeight: 48,
    borderRadius: 16,
    border: "none",
    background: `linear-gradient(135deg, ${BRAND.red}, ${BRAND.redDeep})`,
    color: "#fff",
    fontWeight: 800,
    padding: "0 18px",
    cursor: "pointer",
    boxShadow: "0 16px 28px rgba(185,52,58,0.18)",
  },
  ghostBtn: {
    minHeight: 48,
    borderRadius: 16,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontWeight: 700,
    padding: "0 18px",
    cursor: "pointer",
  },
  textBtn: {
    minHeight: 48,
    border: "none",
    background: "transparent",
    color: BRAND.redDeep,
    fontWeight: 800,
    padding: "0 10px",
    cursor: "pointer",
  },
  filterHint: {
    color: BRAND.muted,
    fontSize: 13,
  },
  errorBanner: {
    padding: "14px 16px",
    borderRadius: 18,
    background: BRAND.blush,
    border: `1px solid ${BRAND.borderStrong}`,
    color: BRAND.redDeep,
    fontWeight: 700,
  },
  highlightGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  visualGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.35fr) minmax(340px, 0.95fr)",
    gap: 18,
    alignItems: "stretch",
  },
  secondaryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 18,
    alignItems: "start",
  },
  chartStack: {
    display: "grid",
    gap: 18,
    alignContent: "start",
  },
  sideStack: {
    display: "grid",
    gap: 18,
    alignContent: "start",
  },
  chartPanel: {
    padding: 22,
    borderRadius: 28,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 18px 40px ${BRAND.shadow}`,
    display: "grid",
    gap: 18,
    overflow: "hidden",
  },
  chartHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  chartTitle: {
    margin: 0,
    color: BRAND.text,
    fontSize: 24,
    fontWeight: 800,
  },
  chartSub: {
    margin: "6px 0 0",
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  chartSvg: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  chartCanvas: {
    position: "relative",
  },
  chartTooltip: {
    position: "absolute",
    transform: "translate(-50%, calc(-100% - 12px))",
    minWidth: 180,
    maxWidth: 260,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(37,41,53,0.96)",
    color: "#fff",
    boxShadow: "0 16px 32px rgba(37,41,53,0.18)",
    pointerEvents: "none",
    zIndex: 2,
    display: "grid",
    gap: 4,
  },
  chartTooltipLine: {
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  chartTooltipSub: {
    fontSize: 11,
    lineHeight: 1.4,
    color: "rgba(255,255,255,0.72)",
  },
  chartEmpty: {
    padding: 18,
    borderRadius: 18,
    background: BRAND.sand,
    border: `1px dashed ${BRAND.borderStrong}`,
    color: BRAND.muted,
  },
  legendRow: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  legendItem: {
    display: "inline-flex",
    gap: 8,
    alignItems: "center",
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 999,
    display: "inline-block",
  },
  productivityHeaderChip: {
    display: "inline-flex",
    alignItems: "center",
    padding: "8px 12px",
    borderRadius: 999,
    background: BRAND.blueSoft,
    color: BRAND.blue,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  productivityHeaderActions: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  infoButton: {
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.redDeep,
    width: 34,
    height: 34,
    borderRadius: 999,
    display: "grid",
    placeItems: "center",
    fontSize: 15,
    fontWeight: 800,
    cursor: "pointer",
    userSelect: "none",
  },
  helpCard: {
    padding: 16,
    borderRadius: 18,
    background: BRAND.ivory,
    border: `1px solid ${BRAND.borderStrong}`,
    display: "grid",
    gap: 10,
  },
  infoTitle: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 800,
  },
  infoText: {
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },
  infoList: {
    display: "grid",
    gap: 6,
    color: BRAND.text,
    fontSize: 13,
    lineHeight: 1.5,
  },
  productivityList: {
    display: "grid",
    gap: 16,
  },
  productivityRow: {
    display: "grid",
    gap: 10,
    padding: 16,
    borderRadius: 22,
    background: `linear-gradient(180deg, ${BRAND.ivory}, ${BRAND.sand})`,
    border: `1px solid ${BRAND.border}`,
  },
  productivityIdentity: {
    display: "grid",
    gap: 4,
  },
  productivityName: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 800,
  },
  productivityMeta: {
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.5,
  },
  productivityBarGroup: {
    display: "grid",
    gridTemplateColumns: "auto 1fr",
    gap: 12,
    alignItems: "center",
  },
  productivityValue: {
    color: BRAND.text,
    fontSize: 18,
    fontWeight: 800,
    minWidth: 46,
  },
  productivityTrack: {
    width: "100%",
    height: 12,
    borderRadius: 999,
    background: "rgba(37,41,53,0.08)",
    overflow: "hidden",
  },
  productivityFill: {
    height: "100%",
    borderRadius: 999,
    background: `linear-gradient(90deg, ${BRAND.red}, ${BRAND.green})`,
    boxShadow: "0 10px 18px rgba(37,41,53,0.14)",
  },
  workloadButton: {
    padding: 0,
    border: "none",
    background: "transparent",
    cursor: "pointer",
    textAlign: "left",
  },
  workloadHint: {
    color: BRAND.blue,
    fontSize: 12,
    fontWeight: 700,
  },
  profileSpotlightList: {
    display: "grid",
    gap: 14,
  },
  profileSpotlightRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 14,
    alignItems: "center",
    padding: 14,
    borderRadius: 20,
    background: `linear-gradient(180deg, ${BRAND.ivory}, ${BRAND.sand})`,
    border: `1px solid ${BRAND.border}`,
  },
  profileSpotlightIdentity: {
    display: "grid",
    gridTemplateColumns: "56px minmax(0, 1fr)",
    gap: 12,
    alignItems: "center",
    minWidth: 0,
  },
  profileSpotlightPhoto: {
    width: 56,
    height: 56,
    borderRadius: 18,
    objectFit: "cover",
    background: BRAND.blush,
    border: `1px solid ${BRAND.border}`,
  },
  profileSpotlightAvatar: {
    width: 56,
    height: 56,
    borderRadius: 18,
    display: "grid",
    placeItems: "center",
    color: "#fff",
  },
  profileSpotlightInitials: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: "0.04em",
  },
  profileSpotlightCopy: {
    display: "grid",
    gap: 4,
    minWidth: 0,
  },
  profileSpotlightName: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.3,
  },
  profileSpotlightMeta: {
    color: BRAND.muted,
    fontSize: 12,
    lineHeight: 1.5,
  },
  profileSpotlightCount: {
    display: "grid",
    justifyItems: "end",
    gap: 2,
    minWidth: 64,
  },
  profileSpotlightCountValue: {
    color: BRAND.text,
    fontSize: 22,
    fontWeight: 800,
  },
  profileSpotlightCountLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  backlogSnapshotGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 12,
  },
  backlogSnapshotCard: {
    padding: 14,
    borderRadius: 18,
    background: `linear-gradient(180deg, ${BRAND.ivory}, ${BRAND.sand})`,
    border: `1px solid ${BRAND.border}`,
    display: "grid",
    gap: 6,
  },
  backlogSnapshotLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  backlogSnapshotValue: {
    color: BRAND.text,
    fontSize: 28,
    fontWeight: 800,
  },
  backlogSnapshotHint: {
    color: BRAND.muted,
    fontSize: 12,
    lineHeight: 1.5,
  },
  backlogAgingList: {
    display: "grid",
    gap: 14,
  },
  backlogAgingRow: {
    display: "grid",
    gridTemplateColumns: "160px minmax(0, 1fr) auto",
    gap: 14,
    alignItems: "center",
  },
  backlogAgingMeta: {
    display: "grid",
    gap: 4,
  },
  backlogAgingLabel: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 800,
  },
  backlogAgingHint: {
    color: BRAND.muted,
    fontSize: 12,
  },
  backlogAgingTrack: {
    width: "100%",
    height: 14,
    borderRadius: 999,
    background: "rgba(37,41,53,0.08)",
    overflow: "hidden",
  },
  backlogAgingFill: {
    height: "100%",
    display: "flex",
    borderRadius: 999,
    overflow: "hidden",
  },
  backlogSegment: {
    height: "100%",
  },
  backlogAgingSplit: {
    minWidth: 110,
    textAlign: "right",
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
  },
  alertList: {
    display: "grid",
    gap: 14,
  },
  alertCard: {
    padding: 16,
    borderRadius: 20,
    background: `linear-gradient(180deg, ${BRAND.ivory}, ${BRAND.sand})`,
    border: `1px solid ${BRAND.border}`,
    display: "grid",
    gap: 10,
  },
  alertHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  alertTitle: {
    color: BRAND.text,
    fontSize: 15,
    fontWeight: 800,
  },
  alertMeta: {
    color: BRAND.muted,
    fontSize: 12,
    marginTop: 4,
  },
  alertBadgeCritical: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 10px",
    borderRadius: 999,
    background: BRAND.blush,
    color: BRAND.redDeep,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  alertBadgeWarning: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 10px",
    borderRadius: 999,
    background: BRAND.amberSoft,
    color: BRAND.amber,
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  alertBody: {
    color: BRAND.text,
    fontSize: 14,
    lineHeight: 1.6,
  },
  alertBreakdown: {
    color: BRAND.muted,
    fontSize: 12,
    lineHeight: 1.5,
    fontWeight: 700,
  },
  ringGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 14,
  },
  ringCard: {
    display: "grid",
    justifyItems: "center",
    gap: 10,
    padding: 10,
    borderRadius: 22,
    background: BRAND.ivory,
    border: `1px solid ${BRAND.border}`,
  },
  ringSvg: {
    width: "100%",
    maxWidth: 180,
    height: "auto",
  },
  ringSub: {
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.6,
    textAlign: "center",
  },
  stageList: {
    display: "grid",
    gap: 14,
  },
  stageRow: {
    display: "grid",
    gap: 8,
  },
  stageMeta: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
  },
  stageLabel: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 700,
  },
  stageValue: {
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  stageTrack: {
    width: "100%",
    height: 10,
    borderRadius: 999,
    background: "rgba(37,41,53,0.08)",
    overflow: "hidden",
  },
  stageFill: {
    height: "100%",
    borderRadius: 999,
  },
  rankList: {
    display: "grid",
    gap: 16,
  },
  rankRow: {
    display: "grid",
    gap: 10,
  },
  rankLabel: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 800,
  },
  rankBars: {
    display: "grid",
    gap: 8,
  },
  rankMetric: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 10,
    alignItems: "center",
  },
  rankTrack: {
    width: "100%",
    height: 10,
    borderRadius: 999,
    background: "rgba(37,41,53,0.08)",
    overflow: "hidden",
  },
  rankFill: {
    height: "100%",
    borderRadius: 999,
  },
  rankValue: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    minWidth: 34,
    textAlign: "right",
  },
  statStack: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 14,
  },
  statBlock: {
    padding: 16,
    borderRadius: 20,
    background: BRAND.sand,
    border: `1px solid ${BRAND.border}`,
    display: "grid",
    gap: 8,
  },
  statLabel: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },
  statNumber: {
    color: BRAND.text,
    fontSize: 28,
    fontWeight: 800,
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  metricCard: {
    padding: 18,
    borderRadius: 22,
    border: "1px solid transparent",
    minHeight: 126,
    boxSizing: "border-box",
  },
  metricLabel: {
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  metricValue: {
    marginTop: 14,
    fontSize: 30,
    fontWeight: 800,
    color: BRAND.text,
  },
  metricHint: {
    marginTop: 10,
    fontSize: 13,
    lineHeight: 1.5,
    color: BRAND.muted,
  },
  panel: {
    padding: 22,
    borderRadius: 28,
    background: BRAND.ivory,
    border: `1px solid ${BRAND.border}`,
    boxShadow: `0 18px 44px ${BRAND.shadow}`,
    display: "grid",
    gap: 16,
  },
  sectionHeader: {
    display: "flex",
    gap: 12,
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },
  sectionTitle: {
    margin: 0,
    fontSize: 26,
    color: BRAND.text,
  },
  sectionSub: {
    margin: "6px 0 0",
    color: BRAND.muted,
    fontSize: 14,
    lineHeight: 1.6,
  },
  sectionMeta: {
    color: BRAND.mutedSoft,
    fontSize: 13,
    fontWeight: 800,
  },
  tableWrap: {
    overflowX: "auto",
    borderRadius: 20,
    border: `1px solid ${BRAND.border}`,
    background: "#fff",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: 840,
  },
  th: {
    textAlign: "left",
    padding: "14px 16px",
    fontSize: 12,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: BRAND.muted,
    background: BRAND.blush,
    borderBottom: `1px solid ${BRAND.border}`,
  },
  row: {
    background: "#fff",
  },
  td: {
    padding: "14px 16px",
    color: BRAND.text,
    borderBottom: `1px solid ${BRAND.border}`,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  userCell: {
    padding: "14px 16px",
    borderBottom: `1px solid ${BRAND.border}`,
    minWidth: 240,
  },
  userButton: {
    display: "grid",
    gap: 6,
    width: "100%",
    border: "none",
    background: "transparent",
    padding: 0,
    textAlign: "left",
    cursor: "pointer",
  },
  userEmail: {
    color: BRAND.text,
    fontWeight: 800,
  },
  userMeta: {
    marginTop: 6,
    color: BRAND.muted,
    fontSize: 13,
  },
  emptyCell: {
    padding: 24,
    textAlign: "center",
    color: BRAND.muted,
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(37,41,53,0.38)",
    display: "grid",
    placeItems: "center",
    padding: 20,
    zIndex: 1000,
  },
  modalCard: {
    width: "min(1180px, 100%)",
    maxHeight: "90vh",
    overflow: "auto",
    display: "grid",
    gap: 18,
    padding: 22,
    borderRadius: 28,
    background: BRAND.ivory,
    border: `1px solid ${BRAND.borderStrong}`,
    boxShadow: "0 28px 60px rgba(37,41,53,0.18)",
  },
  modalHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    alignItems: "flex-start",
  },
  modalEyebrow: {
    color: BRAND.redDeep,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
  },
  modalTitle: {
    margin: "6px 0 0",
    fontSize: 28,
    color: BRAND.text,
  },
  modalSubtitle: {
    margin: "8px 0 0",
    color: BRAND.muted,
    fontSize: 14,
  },
  modalClose: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 24,
    lineHeight: 1,
    cursor: "pointer",
  },
  modalFilterRow: {
    display: "flex",
    gap: 12,
    alignItems: "end",
    flexWrap: "wrap",
  },
  modalExplain: {
    padding: 14,
    borderRadius: 16,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    color: BRAND.muted,
    fontSize: 13,
    lineHeight: 1.6,
  },
  modalField: {
    display: "grid",
    gap: 8,
    minWidth: 220,
  },
  modalBody: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 18,
    alignItems: "start",
  },
  modalPanel: {
    display: "grid",
    gap: 14,
  },
  modalPanelHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  modalPanelTitle: {
    color: BRAND.text,
    fontSize: 18,
    fontWeight: 800,
  },
  modalPanelSub: {
    marginTop: 4,
    color: BRAND.muted,
    fontSize: 12,
    lineHeight: 1.5,
  },
  panelToggle: {
    minHeight: 36,
    borderRadius: 12,
    border: `1px solid ${BRAND.borderStrong}`,
    background: "#fff",
    color: BRAND.text,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    padding: "0 12px",
    cursor: "pointer",
  },
  auditHeaderTools: {
    display: "flex",
    gap: 12,
    alignItems: "end",
    flexWrap: "wrap",
  },
  auditFilterField: {
    display: "grid",
    gap: 8,
    minWidth: 220,
  },
  auditList: {
    display: "grid",
    gap: 12,
  },
  auditItem: {
    padding: 14,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
  },
  auditHead: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    flexWrap: "wrap",
  },
  auditBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "7px 10px",
    borderRadius: 999,
    background: BRAND.blush,
    color: BRAND.redDeep,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
  },
  auditTime: {
    color: BRAND.mutedSoft,
    fontSize: 12,
    fontWeight: 700,
  },
  auditMeta: {
    marginTop: 10,
    color: BRAND.muted,
    fontSize: 13,
    fontWeight: 700,
  },
  auditDetails: {
    marginTop: 12,
  },
  auditSummary: {
    cursor: "pointer",
    color: BRAND.redDeep,
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
  },
  auditChangeList: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
    marginTop: 12,
  },
  auditChangeRow: {
    display: "grid",
    gap: 4,
    alignContent: "start",
    minHeight: 82,
    padding: "10px 12px",
    borderRadius: 14,
    background: BRAND.sand,
    border: `1px solid ${BRAND.border}`,
  },
  auditChangeLabel: {
    color: BRAND.muted,
    fontSize: 11,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  },
  auditChangeValue: {
    color: BRAND.text,
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.5,
    wordBreak: "break-word",
  },
  emptyAudit: {
    padding: 18,
    borderRadius: 18,
    background: BRAND.blush,
    border: `1px dashed ${BRAND.borderStrong}`,
    color: BRAND.muted,
  },
  workloadGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 12,
  },
  workloadCard: {
    padding: 14,
    borderRadius: 18,
    background: "#fff",
    border: `1px solid ${BRAND.border}`,
    display: "grid",
    gap: 8,
  },
  workloadTitle: {
    color: BRAND.text,
    fontSize: 14,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  workloadMeta: {
    color: BRAND.muted,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.5,
  },
  workloadBody: {
    color: BRAND.text,
    fontSize: 13,
    lineHeight: 1.6,
  },
};
